/**
 * Documented access gates used by the UI (and tests). Route-level permissions live in
 * `src/routes.ts`. Write-level gates here match DOCUMENTATION.md §6.1.
 *
 * `adminapp` is a universal bypass, same as `checkRole` on the server.
 */
import { ROUTE_PERMISSIONS } from '../routes';
import { hasAnyRoleToken, hasRoleToken } from './roleTokens';

export const SUPERADMIN_ONLY_ROLES = ['adminapp'] as const;
export const ADMIN_ONLY_ROLES = ['adminapp', 'admin'] as const;
export const EXECUTIVE_ROLES = ['adminapp', 'admin', 'ceo'] as const;
export const FINANCE_ROLES = ['adminapp', 'admin', 'ceo', 'accounting'] as const;
export const OPERATIONS_WRITE_ROLES = ['adminapp', 'admin', 'ceo', 'sales_rep', 'operations'] as const;
export const UPS_OPERATIONS_ROLES = ['operations'] as const;
export const PICKUP_MANAGE_ROLES = ['operations'] as const;
/** Partners API / agency network admin config (design spec §2.4/§3.5) -- server.ts mirrors this
 *  as PARTNER_ADMIN_READ_ROLES/PARTNER_ADMIN_WRITE_ROLES. */
export const PARTNER_ADMIN_READ_ROLES = ['adminapp', 'admin', 'ceo'] as const;
export const PARTNER_ADMIN_WRITE_ROLES = ['adminapp', 'admin'] as const;

export function canAccessRoute(roles: unknown, routeKey: keyof typeof ROUTE_PERMISSIONS): boolean {
  if (hasRoleToken(roles, 'adminapp')) return true;
  const allowed = ROUTE_PERMISSIONS[routeKey];
  if (allowed.length === 0) return true;
  return hasAnyRoleToken(roles, [...allowed]);
}

/** True client (portal), not staff who also happens to have a client token. */
export function isPureClient(roles: unknown): boolean {
  return hasRoleToken(roles, 'client')
    && !hasAnyRoleToken(roles, ['adminapp', 'admin', 'ceo', 'accounting', 'sales_rep', 'operations']);
}

/** True agency operator (design spec §3.5): "nothing else. No visibility into other agencies,
 *  comercial, or finance." An agencia_operador who also holds staff roles sees everything those
 *  roles already grant, same convention as isPureClient/isComercialOnly above. */
export function isPureAgencyOperator(roles: unknown): boolean {
  return hasRoleToken(roles, 'agencia_operador')
    && !hasAnyRoleToken(roles, ['adminapp', 'admin', 'ceo', 'accounting', 'sales_rep', 'operations']);
}

export function canReviewClientDocuments(roles: unknown): boolean {
  return hasRoleToken(roles, 'adminapp') || hasAnyRoleToken(roles, [...FINANCE_ROLES]);
}

export function canSeeCommissionRates(roles: unknown): boolean {
  return hasRoleToken(roles, 'adminapp') || hasAnyRoleToken(roles, [...FINANCE_ROLES]);
}

export function canEditCommissionRates(roles: unknown): boolean {
  return hasAnyRoleToken(roles, [...ADMIN_ONLY_ROLES]);
}

export function canManagePickups(roles: unknown): boolean {
  return hasRoleToken(roles, 'adminapp') || hasAnyRoleToken(roles, [...PICKUP_MANAGE_ROLES]);
}

export function canCreateCarrierShipment(roles: unknown): boolean {
  return hasRoleToken(roles, 'adminapp') || hasAnyRoleToken(roles, [...UPS_OPERATIONS_ROLES]);
}
